import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import './SafetyCoefficients.scss';
import { ISafetyCoefficientsProps, ISafetyCoefficientsForm } from './ISafetyCoefficients';
import * as Yup from 'yup';
import './SafetyCoefficients.scss';
import { ErrorMessage, Field, FormikProvider, useFormik } from 'formik';
import { Divider, Grid } from '@mui/material';
import { useEffect } from 'react';
import { setSafetyCoefficients } from '../../../store/project/projectslice';
import { selectProjectInfo, selectMacro2 } from '../../../../store/projectInfo/projectInfoSlice';
import { updateValidation } from '../../../../Macro2/store/project/resultsSlice';
import { RootState } from '../../../../../reduxService';
import Dialog from '../../../../../components/Dialog/Dialog';
import FieldNumber from '../../../../../components/FieldNumber/FieldNumber';

export const SafetyCoefficientsDefault: ISafetyCoefficientsForm = {
    coefficientForVariableLoads: 1.5,
    coefficientForPermanentLoads: 1.5,
    reductionForMeshResistance: 2.5,
    reductionOnXSpacing: 1.1,
    reductionOnYGradient: 1,
    reductionForCableResistance: 2,
    coefficientSteelBarYield: 1.15,
    coefficientRockGroutAdesion: 1.8,
};


const SafetyCoefficients = (props: ISafetyCoefficientsProps) => {
    const { t } = useTranslation();
    let timer = React.useRef<NodeJS.Timeout | null>(null);

    const projectInfo = useAppSelector(selectProjectInfo);
    const macro2 = useAppSelector(selectMacro2);
    const defaultValues = macro2.project?.data?.safetyCoefficients ?? SafetyCoefficientsDefault;

    const [CurrentDialog, setCurrentDialog] = React.useState<string>('');
    const openDialog = (dialogCode: string) => {
        setCurrentDialog(dialogCode);
    }
    const closeDialog = () => {
        setCurrentDialog('');
    }

    const SafetyCoefficientsSchema = Yup.object().shape({
        coefficientForVariableLoads: Yup.number().min(0.01).required().label(t('safety_macro2_variable_loads', { unit_measure: '' })),
        coefficientForPermanentLoads: Yup.number().min(0.01).required().label(t('safety_macro2_permanent_loads', { unit_measure: '' })),
        reductionForMeshResistance: Yup.number().min(0.01).required().label(t('safety_macro2_mesh_resistance_reduction', { unit_measure: '' })),
        reductionOnXSpacing: Yup.number().min(0.01).required().label(t('safety_macro2_x_spacing_reduction', { unit_measure: '' })),
        //reductionOnYGradient: Yup.number().min(1).max(1.15).required().label(t('safety_macro2_y_spacing_reduction', { unit_measure: '' })),
        reductionForCableResistance: Yup.number().min(0.01).required().label(t('safety_macro2_cable_resistance_reduction', { unit_measure: '' })),
        coefficientSteelBarYield: Yup.number().min(0.01).required().label(t('safety_macro2_steel_bar_yield_stress', { unit_measure: '' })),
        coefficientRockGroutAdesion: Yup.number().min(0.01).required().label(t('safety_macro2_rock_grout_adhesion', { unit_measure: '' })),
    });

    const dispatch = useAppDispatch();
    const dispatchCrest = (values: ISafetyCoefficientsForm) => {
        dispatch(setSafetyCoefficients(values));
    };

    const [selectedCorrosionCrown, setSelectedValue] = React.useState('a');

    const getMeasurementUnit = (unit: string[]) => {
        return ' [' + unit[projectInfo.data?.unit == 'Metric' ? 0 : 1] + ']';
    }

    const formikProps = useFormik({
        initialValues: defaultValues,
        validationSchema: SafetyCoefficientsSchema,
        onSubmit: (values, { setSubmitting }) => {
            dispatchCrest(values);
            setSubmitting(false);
        },
    });

    const handleValueChanged = (valueKey: string, value: any) => {
        formikProps.setFieldValue(valueKey, value);
        dispatchCrest(formikProps.values);
    }

    useEffect(() => {
        dispatchCrest(formikProps.values);
    }, [formikProps.values]);

    const currentResults = useAppSelector((state: RootState) => {
        return { ...state.Macro2.results.data };
    });
    useEffect(() => {
        if (currentResults.validation?.startValidation && !currentResults.validation?.safetyCoefficients) {
            formikProps.submitForm();
            formikProps.validateForm().then((errors) => {
                if (currentResults.validation)
                    dispatch(updateValidation({
                        ...currentResults.validation,
                        safetyCoefficients: true,
                        safetyCoefficientsErrors: Object.keys(errors).length > 0,
                    }));
            });

        }
    }, [JSON.stringify(currentResults.validation)]);

    return (
        <Grid container style={{ alignItems: 'center' }} className='style-form'>
            <FormikProvider value={formikProps}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('safety_macro2_section_rock_slope')}</label>
                    </Grid>
                    <Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('safety_macro2_variable_loads', { unit_measure: '' })}</label>
                            <i onClick={() => { openDialog('variable_loads') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber step="0.01" name='coefficientForVariableLoads' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='coefficientForVariableLoads' component='span' className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className='symbol'>{t('safety_symbols.safety_macro2_variable_loads')}</label>
                    </div>
                    <Dialog
                        open={CurrentDialog == 'variable_loads'}
                        title={`${t('safety_macro2_info_dialogs.variable_loads.title')}`}
                        onClose={closeDialog}>
                        <div dangerouslySetInnerHTML={{ __html: ''+t('safety_macro2_info_dialogs.variable_loads.body')}}>
                        </div>
                    </Dialog>
                    <Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('safety_macro2_permanent_loads', { unit_measure: '' })}</label>
                            <i onClick={() => { openDialog('permanent_loads') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber step="0.01" name='coefficientForPermanentLoads' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='coefficientForPermanentLoads' component='span' className="text-danger" />
                    </Grid>
                    <Dialog
                        open={CurrentDialog == 'permanent_loads'}
                        title={`${t('safety_macro2_info_dialogs.permanent_loads.title')}`}
                        onClose={closeDialog}>
                        <div dangerouslySetInnerHTML={{ __html: ''+t('safety_macro2_info_dialogs.permanent_loads.body')}}>
                        </div>
                    </Dialog>
                    <div className="symbol-wrap">
                        <label className='symbol'>{t('safety_symbols.safety_macro2_permanent_loads')}</label>
                    </div>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('safety_macro2_section_mesh')}</label>
                    </Grid>
                    <Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('safety_macro2_mesh_resistance_reduction', { unit_measure: '' })}</label>
                            <i onClick={() => { openDialog('mesh_resistance_reduction') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber step="0.01" name='reductionForMeshResistance' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='reductionForMeshResistance' component='span' className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className='symbol'>{t('safety_symbols.safety_macro2_mesh_resistance_reduction')}</label>
                    </div>
                    <Dialog
                        open={CurrentDialog == 'mesh_resistance_reduction'}
                        title={`${t('safety_macro2_info_dialogs.mesh_resistance_reduction.title')}`}
                        onClose={closeDialog}>
                        <div dangerouslySetInnerHTML={{ __html: ''+t('safety_macro2_info_dialogs.mesh_resistance_reduction.body')}}>
                        </div>
                    </Dialog>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('safety_macro2_section_geometry_cable')}</label>
                    </Grid>
                    <Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('safety_macro2_x_spacing_reduction', { unit_measure: '' })}</label>
                            <i onClick={() => { openDialog('spacing_reduction') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber step="0.01" name='reductionOnXSpacing' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='reductionOnXSpacing' component='span' className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className='symbol'>{t('safety_symbols.safety_macro2_x_spacing_reduction')}</label>
                    </div>
                    {/*<Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('safety_macro2_y_spacing_reduction', { unit_measure: '' })}</label>
                            <i onClick={() => { openDialog('spacing_reduction') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber step="0.01" name='reductionOnYGradient' className='d-flex justify-content-between align-items-center' />
                        <ErrorMessage name='reductionOnYGradient' component='span' className="text-danger" />
                    </Grid>
                    <Grid item xs={1}>
                        <label className='symbol'>{t('safety_symbols.safety_macro2_y_spacing_reduction')}</label>
                    </Grid>*/}
                    <Dialog
                        open={CurrentDialog == 'spacing_reduction'}
                        title={`${t('safety_macro2_info_dialogs.spacing_reduction.title')}`}
                        onClose={closeDialog}>
                        <div dangerouslySetInnerHTML={{ __html: ''+t('safety_macro2_info_dialogs.spacing_reduction.body')}}>
                        </div>
                    </Dialog>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('safety_macro2_section_cable_strength')}</label>
                    </Grid>
                    <Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('safety_macro2_cable_resistance_reduction', { unit_measure: '' })}</label>
                            <i onClick={() => { openDialog('cable_resistance_reduction') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber step="0.01" name='reductionForCableResistance' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='reductionForCableResistance' component='span' className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className='symbol'>{t('safety_symbols.safety_macro2_cable_resistance_reduction')}</label>
                    </div>
                    <Dialog
                        open={CurrentDialog == 'cable_resistance_reduction'}
                        title={`${t('safety_macro2_info_dialogs.cable_resistance_reduction.title')}`}
                        onClose={closeDialog}>
                        <div dangerouslySetInnerHTML={{ __html: ''+t('safety_macro2_info_dialogs.cable_resistance_reduction.body')}}>
                        </div>
                    </Dialog>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('safety_macro2_section_anchor_type')}</label>
                    </Grid>
                    <Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('safety_macro2_steel_bar_yield_stress', { unit_measure: '' })}</label>
                            <i onClick={() => { openDialog('steel_bar_yield_stress') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber step="0.01" name='coefficientSteelBarYield' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='coefficientSteelBarYield' component='span' className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className='symbol'>{t('safety_symbols.safety_macro2_steel_bar_yield_stress')}</label>
                    </div>
                    <Dialog
                        open={CurrentDialog == 'steel_bar_yield_stress'}
                        title={`${t('safety_macro2_info_dialogs.steel_bar_yield_stress.title')}`}
                        onClose={closeDialog}>
                        <div dangerouslySetInnerHTML={{ __html: ''+t('safety_macro2_info_dialogs.steel_bar_yield_stress.body')}}>
                        </div>
                    </Dialog>
                    <Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('safety_macro2_rock_grout_adhesion', { unit_measure: '' })}</label>
                            <i onClick={() => { openDialog('rock_grout_adhesion') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber step="0.01" name='coefficientRockGroutAdesion' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='coefficientRockGroutAdesion' component='span' className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className='symbol'>{t('safety_symbols.safety_macro2_rock_grout_adhesion')}</label>
                    </div>
                    <Dialog
                        open={CurrentDialog == 'rock_grout_adhesion'}
                        title={`${t('safety_macro2_info_dialogs.rock_grout_adhesion.title')}`}
                        onClose={closeDialog}>
                        <div dangerouslySetInnerHTML={{ __html: ''+t('safety_macro2_info_dialogs.rock_grout_adhesion.body')}}>
                        </div>
                    </Dialog>
                </Grid>
            </FormikProvider>
        </Grid>
    );
};

export default SafetyCoefficients;
